import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ajaxGetPurchaseOrders } from '../../services/purchaseOrderService';
import { ajaxGetSuppliersAutocomplete } from '../../services/supplierService';
import { toDateText, priceFormat } from '../../helpers';
import useWorkspace from '../../hooks/useWorkspace';
import LoadingSpinner from '../../components/LoadingSpinner';
import ViewDownloadIconButton from '../../components/ViewDownloadIconButton';
import Page from '../../components/Page';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import EnhancedTable from '../../components/EnhancedTable'
import Grid from '@mui/material/Grid';
import StyledSearchDate from '../../components/StyledSearchDate';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import inMemoryUser from '../../services/inMemoryUser';
import Tooltip from '@mui/material/Tooltip';

const dateFormat = (v) => v ? toDateText(v) : '-';

export default function PurchaseOrderList() {
  const [workspace, setWorkspace] = useWorkspace(true, 'purchaseOrderPage', { table :{}, filter: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState(workspace.filter.searchTerm || '');
  const [startDate, setStartDate] = useState(workspace.filter.startDate || null);
  const [endDate, setEndDate] = useState(workspace.filter.endDate || null);
  const [status, setStatus] = useState(workspace.filter.status || 'all');
  const [paidStatus, setPaidStatus] = useState(workspace.filter.paidStatus || 'all');
  const [autocomplete, setAutocomplete] = useState(workspace.filter.autocomplete || null);
  const [autocompleteList, setAutocompleteList] = useState([]);

  const navigate = useNavigate();

  const onTableParams = useCallback((params) => {
    if ( isLoading ) {
      return;
    }
    const {pageItems, ...restParams} = params;
    setWorkspace(prev => ({
      ...prev,
      table: {
        ...prev.table,
        ...restParams
      }
    }));
  }, [isLoading, setWorkspace]);

  useEffect(() => {
    if ( isLoading ) {
      return;
    }
    setWorkspace(prev => ({
      ...prev,
      filter: {
        ...prev.filter,
        searchTerm, startDate, endDate,
        status, paidStatus, autocomplete
      }
    }));
  }, [
    isLoading, setWorkspace,
    searchTerm, startDate, endDate,
    status, paidStatus, autocomplete
  ]);

  useEffect(() => {
    ajaxGetPurchaseOrders({})
      .then((res) => {
        setItems(res.data);
      })
      .then(() => {
        return ajaxGetSuppliersAutocomplete().then((supplierRes) => {
          const supplierList = supplierRes.data;
          const autocompleteList = [
            { id: -1, name: 'All Suppliers' },
            ...supplierList.map(s => ({ id: s.id, name: s.name }))
          ]
          setAutocompleteList(autocompleteList);
          if ( !autocomplete || !autocompleteList.find(s => s.id === autocomplete.id) ) {
            setAutocomplete(autocompleteList.find(s => s.id === -1));
          }
          setIsLoading(false);
        }).catch(() => {
          setAutocomplete(null);
          setIsLoading(false);
        });
      })
      .catch(() => {
        setItems([]);
        setIsLoading(false);
      });
  }, [autocomplete]);

  const onClearFilter = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setStatus('all');
    setPaidStatus('all');
    setAutocomplete(autocompleteList.find(s => s.id === -1));
  };

  const handleSupplierSearch = (event, value) => {
    setAutocomplete(value);
  };

  const searchChangeHandle = (event) => {
    const new_value = event.target.value.toLowerCase();
    setSearchTerm(new_value);
  };

  useEffect(() => {
    const results = items.filter((item) => {
      let testStartDate = true;
      let testEndDate = true;
      let searchByTerm = true;
      let testStatus = true;
      let testSupplier = true;
      let testPaidStatus = true;

      if(searchTerm) {
        const fields = ['po_number', 'purpose', 'requisitioner']
        searchByTerm = fields.reduce((acc, field) => acc || (item[field] && item[field].toLowerCase().includes(searchTerm)), false); 
      }

      if (startDate) {
        testStartDate = item.po_date >= startDate;
      }
      if (endDate) {
        testEndDate = item.po_date <= endDate;
      }

      if (status !== 'all') {
        testStatus = item.status === status;
      }

      if (paidStatus !== 'all') {
        testPaidStatus = item.paid === (paidStatus === 'paid' ? 1 : 0);
      }

      if (autocomplete && autocomplete.id !== -1) {
        testSupplier = item.supplier_name === autocomplete.name;
      }
      
      return testStartDate && testEndDate && searchByTerm && testStatus && testSupplier && testPaidStatus;
    });

    setSearchResults(results);
  }, [searchTerm, items, startDate, endDate, status, paidStatus, autocomplete]);

  const columns = [
    {
      id: 'po_date',
      label: 'PO Date',
      minWidth: 100,
      format: dateFormat,
      sortable: true
    },
    {
      id: 'po_number',
      label: 'PO Number',
      minWidth: 100,
      sortable: true
    },
    {
      id: 'supplier_name',
      label: 'Supplier',
      sortable: true
    },
    {
      id: 'requisitioner',
      label: 'Requisitioner',
      sortable: true
    },
    {
      id: 'purpose',
      label: 'Purpose',
      sortable: true
    },
    {
      id: 'department',
      label: 'Department',
      sortable: true
    },
    {
      id: 'due_date',
      label: 'Due Date',
      minWidth: 100,
      format: dateFormat,
      sortable: true
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: true,
      format: (value, row) =>
        <div style={{ textAlign: 'center' }}>
          {value === 'cancelled' ? (
            <Tooltip
              title={
                <React.Fragment>
                  <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                    {row.cancelled_reason || 'No reason provided'}
                  </div>
                </React.Fragment>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    '& .MuiTooltip-arrow': {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                },
              }}
            >
              <Chip
                sx={{color: value === 'draft' ? '#000' : '#fff', height: '18px', fontSize: '12px'}}
                label={
                  value === 'draft' ? 'Draft' :
                  value === 'waitingapproval' ? 'Waiting Approval' :
                  value === 'approved' ? 'Approved' :
                  value === 'cancelled' ? 'Cancelled' :
                  value === 'rejected' ? 'Rejected' :
                  value === 'received' ? 'Received' :
                  'Unknown'
                }
                color={
                  value === 'draft' ? 'default' :
                  value === 'waitingapproval' ? 'warning' :
                  value === 'approved' ? 'success' :
                  value === 'cancelled' ? 'error' :
                  value === 'rejected' ? 'error' :
                  value === 'received' ? 'info' :
                  'default'
                }
                size="small"
              />
            </Tooltip>
          ) : (
            <Chip
              sx={{color: value === 'draft' ? '#000' : '#fff', height: '18px', fontSize: '12px'}}
              label={
                value === 'draft' ? 'Draft' :
                value === 'waitingapproval' ? 'Waiting Approval' :
                value === 'approved' ? 'Approved' :
                value === 'cancelled' ? 'Cancelled' :
                value === 'rejected' ? 'Rejected' :
                value === 'received' ? 'Received' :
                'Unknown'
              }
              color={
                value === 'draft' ? 'default' :
                value === 'waitingapproval' ? 'warning' :
                value === 'approved' ? 'success' :
                value === 'cancelled' ? 'error' :
                value === 'rejected' ? 'error' :
                value === 'received' ? 'info' :
                'default'
              }
              size="small"
            />
          )}
        </div>
    },
    {
      id: 'amount',
      label: 'Amount',
      minWidth: 100,
      sortable: true,
      format: (value) => priceFormat(value, 2, 6),
      summary: (item, column) => item[column.id] || 0
    },
    {
      id: 'paid',
      label: 'Paid',
      minWidth: 100,
      sortable: true,
      format: (value) =>
        value === 1 ?
          <div style={{ textAlign: 'center' }}>
            <Chip
              sx={{color: '#fff', height: '18px', fontSize: '12px'}}
              label={'Paid'}
              color={'info'}
              size="small"
            />
          </div>
        :
        <div />
    },
    {
      id: 'proof_payment_pdf',
      label: 'Proof of Payment',
      minWidth: 100,
      format: (value, row) => (
        row.paid === 1 ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order/proof_payment_pdf"
            fileName={row.po_number + '-proof-payment'}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    },
    {
      id: 'supplier_quote_pdf',
      label: 'Supplier Quote',
      minWidth: 100,
      format: (value, row) => (
        row.supplier_quote_pdf ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order/supplier_quote_pdf"
            fileName={row.po_number + '-supplier-quote'}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    },
    {
      id: 'po_pdf',
      label: 'PO PDF',
      minWidth: 100,
      format: (value, row) => (
        row.status === 'approved' || row.status === 'received' ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order/po_pdf"
            fileName={row.po_number}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    },
  ];

  return (
    <Page title="Purchase Orders" maxWidth={false} sx={{ display: 'flex', flexDirection: 'column' }}>
      {
        isLoading ?
        <LoadingSpinner />
        :
        <React.Fragment>
          <Box>
            <Grid container spacing={1}>
              <Grid xs={12} sm={4} item>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  label="Search"
                  placeholder="Search keyword"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  onChange={searchChangeHandle}
                  value={searchTerm}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <Autocomplete
                  disablePortal
                  sx={{ width: '100%' }}
                  size="small"
                  value={autocomplete}
                  options={autocompleteList}
                  isOptionEqualToValue={(option, value) => (value && option.id === value.id)}
                  getOptionLabel={(option) => `${option.name} [${option.id}]`}
                  onChange={handleSupplierSearch}
                  renderInput={(params) => <TextField {...params} label="Supplier Name" />}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <StyledSearchDate
                  startDate={startDate}
                  endDate={endDate}
                  onStartDate={setStartDate}
                  onEndDate={setEndDate}
                  gridProps={{ columnSpacing: 1 }}
                />
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControl sx={{ mt: '10px' }}>
                  <FormLabel id="status-label">Status</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="status-label"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <FormControlLabel value="all" control={<Radio size="small"/>} label="All" />
                    <FormControlLabel value="draft" control={<Radio size="small"/>} label="Draft" />
                    <FormControlLabel value="waitingapproval" control={<Radio size="small"/>} label="Waiting Approval" />
                    <FormControlLabel value="approved" control={<Radio size="small"/>} label="Approved" />
                    <FormControlLabel value="received" control={<Radio size="small"/>} label="Received" />
                    <FormControlLabel value="rejected" control={<Radio size="small"/>} label="Rejected" />
                    <FormControlLabel value="cancelled" control={<Radio size="small"/>} label="Cancelled" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={4} item>
                <FormControl sx={{ mt: '10px' }}>
                  <FormLabel id="paid-status-label">Payment Status</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="paid-status-label"
                    value={paidStatus}
                    onChange={(e) => setPaidStatus(e.target.value)}
                  >
                    <FormControlLabel value="all" control={<Radio size="small"/>} label="All" />
                    <FormControlLabel value="paid" control={<Radio size="small"/>} label="Paid" />
                    <FormControlLabel value="notpaid" control={<Radio size="small"/>} label="Not Paid" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {
                (inMemoryUser.isAdmin() || inMemoryUser.isPurchase()) && (
                <Box>
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    sx={{ margin: '10px 0' }}
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/purchaseorders/add')}
                  >
                    New Purchase Order
                  </Button>
                </Box>
              )}
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ margin: '10px 0' }}
                  startIcon={<FilterListOffIcon />}
                  onClick={onClearFilter}
                >
                  Clear Filter
                </Button>
              </Box>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: '0px 0px 10px 0px' }}>
              <EnhancedTable
                border
                fullHeight
                stickyColumn
                fontSize="12px"
                columns={columns}
                items={searchResults}
                onRowClick={(id) => navigate('/purchaseorders/edit/' + id)}
                order={workspace.table.order || 'desc'}
                orderBy={workspace.table.orderBy || 'po_date'}
                page={workspace.table.pageIndex || 0}
                rowsPerPage={workspace.table.rowsPerPage || 50}
                onTableParams={onTableParams}
              />
            </Box>
          </Box>
        </React.Fragment>
      }
    </Page>
  );
} 